import { useEffect, useRef, useState } from 'react'
import { Button, Group, Modal, Text } from '@mantine/core'
import { useIdle } from '@mantine/hooks'
import { useDispatch, useSelector } from 'react-redux'
import backend from '../../backend/backend'
import { sessionExpired } from '../../store/authActionCreators'
import Message from '../../i18n/Message'

const WARN_SECONDS = 60
const PING_EVERY_MS = 2 * 60 * 1000
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']

/**
 * Warns 60 seconds before the server's idle timeout, pings keepalive at most every 2 minutes while
 * the user is active, and signs out on expiry.
 */
export default function IdleGuard() {
  const dispatch = useDispatch()
  const minutes = useSelector((s) => s.auth.settings.idleTimeoutMinutes) || 15
  const idle = useIdle(Math.max(10, minutes * 60 - WARN_SECONDS) * 1000, { events: EVENTS })
  const [left, setLeft] = useState(WARN_SECONDS)
  const lastPing = useRef(Date.now())

  useEffect(() => {
    const ping = () => {
      if (Date.now() - lastPing.current < PING_EVERY_MS) return
      lastPing.current = Date.now()
      backend.keepAlive().catch(() => {})
    }
    EVENTS.forEach((e) => window.addEventListener(e, ping, { passive: true }))
    return () => EVENTS.forEach((e) => window.removeEventListener(e, ping))
  }, [])

  useEffect(() => {
    if (!idle) {
      setLeft(WARN_SECONDS)
      return undefined
    }
    const timer = setInterval(() => setLeft((n) => n - 1), 1000)
    return () => clearInterval(timer)
  }, [idle])

  useEffect(() => {
    if (idle && left <= 0) dispatch(sessionExpired())
  }, [idle, left, dispatch])

  const stay = () => {
    lastPing.current = Date.now()
    backend.keepAlive().catch(() => dispatch(sessionExpired()))
    setLeft(WARN_SECONDS)
  }

  return (
    <Modal opened={idle && left > 0} onClose={stay} title={<Message id="idle_title" />} centered closeOnClickOutside={false}>
      <Text mb="md">
        <Message id="idle_body" values={{ seconds: left }} />
      </Text>
      <Group justify="flex-end">
        <Button onClick={stay}>
          <Message id="idle_stay" />
        </Button>
      </Group>
    </Modal>
  )
}
